import { AdvisorServicesAPI, computed, debug, NetbankingAPI } from "aaa";
import { compact, flatMap } from "lodash";
import { action, observable } from "mobx";
import { AdvisorServicesAPIClient, NetbankingAPIClient } from "network/APIClient";
import { AccountsStore } from "stores/AccountsStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { OffersStore } from "stores/OffersStore";
import trackingStore from "stores/TrackingStore";
import { IAccount, IBuildingsAccount, IBuildingsPayoutInfo, IBuildingsProlongationInfo } from "types/account";
import { IOffer } from "types/offer";
import { ISigningInfo } from "types/signing";

const preferredTopics = ["SPAVO", "VERAN", "ABER"];
const trackableFlags = [
    "newContract",
    "cancelAndNewContract",
    "cancelAndNewContract30",
    "continueWithBonus",
    "continueWithBonusLegalGuardian",
    "advisorAppointment",
    "payout",
    "payoutLegalGuardian"
];

export class BSPOptionsStore {
    @observable accountsStore?: AccountsStore;
    @observable offersStore: OffersStore;
    @observable _isLoading = true;
    @observable buildingsAccount?: IBuildingsAccount;
    @observable buildingsOffer?: IOffer;
    @observable prolongationDetails?: IBuildingsProlongationInfo["prolongationDetails"];
    @observable availableOptions: string[] = [];
    @observable payoutInfo?: IBuildingsPayoutInfo;

    @observable canMakeNewContract = false;
    @observable canMakeNewContractAndPayout = false;
    @observable canMakeNewContractAndPayout30 = false;
    @observable canContinueWithBonus = false;
    @observable canContinueWithBonusLegalGuardian = false;
    @observable canBookAppointment = false;
    @observable canMakePayout = false;
    @observable canMakePayoutLegalGuardian = false;

    @observable selectedOption?: "continueWithBonus" | "continueWithBonusLegalGuardian";
    @observable selectedPayout?: "payout" | "payoutLegalGuardian";

    @observable signInfo?: ISigningInfo;

    // Needed only for platforms using PdfViewer component for rendering (currently only Android)
    pdfData?: Uint8Array;

    constructor() {
        this.offersStore = new OffersStore();
    }

    @computed get isLoading() {
        return this._isLoading || this.offersStore.isLoading;
    }

    @action async loadData() {
        try {
            this._isLoading = true;

            const contractId = externalInterfaceStore.contractId;
            if (!contractId) {
                return;
            }

            const buildingsTarget = NetbankingAPI.getBuildingsAccounts();
            const buildingsAccountsData = await NetbankingAPIClient.requestType(buildingsTarget);
            this.buildingsAccount = buildingsAccountsData.find(buildingsAccount => buildingsAccount.id === contractId);

            this.canMakeNewContract = !!this.buildingsAccount?.flags.some(flag => flag === "newContract");
            this.canMakeNewContractAndPayout = !!this.buildingsAccount?.flags.some(flag => flag === "cancelAndNewContract");
            this.canMakeNewContractAndPayout30 = !!this.buildingsAccount?.flags.some(flag => flag === "cancelAndNewContract30");
            this.canContinueWithBonus = !!this.buildingsAccount?.flags.some(flag => flag === "continueWithBonus");
            this.canContinueWithBonusLegalGuardian = !!this.buildingsAccount?.flags.some(flag => flag === "continueWithBonusLegalGuardian");
            this.canBookAppointment = !!this.buildingsAccount?.flags.some(flag => flag === "advisorAppointment");
            this.canMakePayout = !!this.buildingsAccount?.flags.some(flag => flag === "payout");
            this.canMakePayoutLegalGuardian = !!this.buildingsAccount?.flags.some(flag => flag === "payoutLegalGuardian");

            let eventLabel = this.buildingsAccount?.flags.sort().reduce((acc, flag) => {
                if (trackableFlags.includes(flag)) {
                    return acc === "" ? flag : `${acc}_${flag}`;
                }
                return acc;
            }, "");
            await trackingStore.trackEvent({ eventName: "bspOptionsOverview", productKey: eventLabel });

            if (
                this.canMakeNewContract
                || this.canMakeNewContractAndPayout
                || this.canMakeNewContractAndPayout30
            ) {
                await this.offersStore.load();

                const buildingsOffer = this.offersStore.offers?.find(offer =>
                    offer.channelProduct === "SBAU" && (offer.conditionsGroupCode === "PLAN" || offer.conditionsGroupCode === "FLEX")
                );

                if (!buildingsOffer) {
                    // Do not show cards when there is no offer linked to the customer
                    this.canMakeNewContract = false;
                    this.canMakeNewContractAndPayout = false;
                    this.canMakeNewContractAndPayout30 = false;
                } else {
                    this.buildingsOffer = buildingsOffer;
                }

            }

            // continueWithBonus and continueWithBonusLegalGuardian do not need an offer
            if (this.canContinueWithBonus || this.canContinueWithBonusLegalGuardian) {
                const target = NetbankingAPI.getBuildingsProlongationInfo(contractId);
                this.prolongationDetails = (await NetbankingAPIClient.requestType(target)).prolongationDetails;
            }

            if (this.canBookAppointment) {
                const topicsTarget = AdvisorServicesAPI.getTopics();
                const topicsData = await AdvisorServicesAPIClient.requestType(topicsTarget);
                const topics = topicsData.topics;

                // get the first matching topic of preferredTopics and set it for appointment feature
                externalInterfaceStore.topicId = flatMap(preferredTopics, validTopic => topics.find(topic => topic.id === validTopic))?.[0]?.id;
            }

            this._isLoading = false;
        } catch (error) {
            debug.log("[BSPOptionsStore] loadData error:", error);

            throw error;
        }
    }

    @action async loadPayoutData() {
        try {
            this._isLoading = true;

            const contractId = externalInterfaceStore.contractId;
            if (!contractId) {
                return;
            }

            const buildingsAccountTarget = NetbankingAPI.getBuildingsAccountById(contractId);
            const payoutTarget = NetbankingAPI.getBuildingsAccountPayoutInfoById(contractId);

            const [buildingsAccount, payoutInfo] = await Promise.all([
                NetbankingAPIClient.requestType(buildingsAccountTarget),
                NetbankingAPIClient.requestType(payoutTarget)
            ]);

            if (!buildingsAccount) {
                debug.log("[BSPOptionsStore] loadPayoutData buildingsAccount not found");
                return;
            }

            if (!payoutInfo) {
                debug.log("[BSPOptionsStore] loadPayoutData payoutInfo not found");
                return;
            }

            // TODO: remove selectedPayout check when code possible function is available in the response
            if (!this.selectedPayout && buildingsAccount.codePossibleFunction !== "payout" && buildingsAccount.codePossibleFunction !== "payoutLegalGuardian") {
                debug.log("[BSPOptionsStore] loadPayoutData codePossibleFunction not found");
                return;
            }

            this.selectedPayout = this.selectedPayout || buildingsAccount.codePossibleFunction === "payout" ? "payout" : "payoutLegalGuardian";
            this.buildingsAccount = buildingsAccount;
            this.payoutInfo = payoutInfo;
            this.initAccountStore(payoutInfo.payoutAccounts);
        } catch (error) {
            debug.log("[BSPOptionsStore] loadPayoutData error:", error);
            throw error;
        } finally {
            this._isLoading = false;
        }
    }

    initAccountStore(accounts: IAccount[]) {
        const sepaAccount = accounts.find(account => account.flags?.includes("contractSepaAcount")); // FIX: Typo???
        const otherAccounts = accounts.filter(account => account !== sepaAccount);

        const sortedAccounts = compact([sepaAccount, ...otherAccounts]);

        this.accountsStore = new AccountsStore(sortedAccounts);
    }

    @action async postProlongation() {
        if (this.buildingsAccount) {
            try {
                this._isLoading = true;

                const target = NetbankingAPI.postBuildingsProlongation(this.buildingsAccount.id);
                const response = await NetbankingAPIClient.requestType(target);

                this.signInfo = response.signInfo;

                this._isLoading = false;
            } catch (error) {
                this._isLoading = false;
                throw error;
            }
        }
    }

    @action async postPayout() {
        if (this.buildingsAccount && this.accountsStore?.selectedAccount) {
            try {
                this._isLoading = true;

                const target = NetbankingAPI.postBuildingsPayout(this.buildingsAccount.id, this.accountsStore.selectedAccount.id);
                const response = await NetbankingAPIClient.requestType(target);

                this.signInfo = response.signInfo;

                this._isLoading = false;
            } catch (error) {
                this._isLoading = false;
                throw error;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/BSPOptions/BSPOptionsStore.tsx